import styled from '@emotion/styled';
import { Send } from '@mui/icons-material';
import { Chip, Grid } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { LuReply, LuX } from 'react-icons/lu';
import {
  EmailLog,
  fileUploadToS3,
  getPresignedURLForPut,
  IBulkEmailAttachment,
  sentBulkEmailCampaignReplyMail,
} from 'wisestep-api';
import {
  AttachmentIcon,
  FileInput,
  getDateUsingFormat,
  useWsEditor,
  WsButton,
  WsColors,
  WsEditor,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

const EmailFooter = styled(Grid)(() => ({
  backgroundColor: WsColors.TertiaryShades50,
  overflowX: 'scroll',
}));

export const getToEmailAddress = (emailLog: EmailLog): string[] =>
  emailLog.direction === 'OUT_BOUND' ? emailLog.to : [emailLog.from];

const handleBody = (replyBody: string, prevConversation: string, emailLog: EmailLog): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(prevConversation ?? '', 'text/html');
  const quoteDiv = doc.querySelector('div.gmail_quote.gmail_quote_container') as HTMLElement;
  if (quoteDiv) {
    quoteDiv.classList.remove('gmail_quote_container');
  }
  let updatedReplyBody = '';
  updatedReplyBody =
    `<div dir="ltr">${replyBody}</div> </br>` +
    ` 
      <div class="gmail_quote gmail_quote_container">
      <div dir="ltr" class="gmail_attr">On ${getDateUsingFormat(
        emailLog.createdOn.toString(),
        'ddd, MMM D, YYYY [at] h:mm A'
      )} &lt;<a href="mailto:${getToEmailAddress(emailLog)[0]}">${
      getToEmailAddress(emailLog)[0]
    }</a>&gt; wrote:<br></div>
      <blockquote class="gmail_quote" style="margin:0px 0px 0px 0.8ex;border-left:1px solid rgb(204,204,204);padding-left:1ex">${
        doc.body.innerHTML
      }</blockquote>
      </div>`;

  return `<div dir="ltr">${updatedReplyBody}</div>`;
};

export const EmailReply = ({ emailLog }: { emailLog: EmailLog }) => {
  const [enabledReply, setEnabledReply] = useState<boolean>(false);
  const [body, setBody] = useState<string>('');
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [attachments, setAttachments] = useState<IBulkEmailAttachment[]>([]);
  const [showAllAttachments, setShowAllAttachments] = useState<boolean>(false);
  const editor = useWsEditor(
    body,
    (htmlBody) => setBody(htmlBody ?? ''),
    () => {},
    () => {}
  );
  const handleReplyEnabled = () => setEnabledReply((prev) => !prev);
  const cancelReply = () => {
    handleReplyEnabled();
    setBody('');
    editor?.commands.setContent('');
  };

  const handleSendMail = async () => {
    try {
      await sentBulkEmailCampaignReplyMail({
        to: getToEmailAddress(emailLog),
        cc: emailLog.cc,
        bcc: emailLog.bcc,
        subject: emailLog.subject,
        body: handleBody(body, emailLog.body, emailLog),
        jobId: emailLog.jobId,
        campaignCode: emailLog.campaignCode,
        replyTo: emailLog.messageId,
        references: emailLog.references + ' ' + (emailLog.messageId ?? ''),
        attachments: attachments,
      } as EmailLog);
      window.location.reload();
      setNotification(success('Email Sent Successfully'));
    } catch (e) {
      setNotification(failed('Something Went wrong!'));
      console.error(e);
    }
  };

  const handleAttachments = (ev: ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files && ev.target.files[0].size < 25000000) {
      ev.target.files && upload(ev.target.files[0]);
    }
  };

  const upload = async (file: File) => {
    let response;
    try {
      response = await getPresignedURLForPut({ fileName: file.name, fileType: file.type });
    } catch (err: any) {
      setNotification(failed(err.message));
    }
    try {
      await fileUploadToS3(file, response.data.preSignedUrl);
      setAttachments((prev) => [
        ...prev,
        {
          attachementName: file.name,
          attachementURL: response.data.preSignedUrl.split('?')[0],
        },
      ]);
      setNotification(success('Uploaded attachment successfully!'));
    } catch (err: any) {
      setNotification(failed(err.message));
    }
  };

  const onDeleteAttachment = (deletedAttachementIndex: number) => {
    const updatedAttachments = attachments.filter((_, index) => index !== deletedAttachementIndex);
    setAttachments(updatedAttachments);
  };

  return (
    <Grid container display="flex" alignItems="center" rowGap={1}>
      <Grid item>
        {!enabledReply ? (
          <WsButton size="small" startIcon={<LuReply />} onClick={handleReplyEnabled}>
            Reply
          </WsButton>
        ) : (
          <WsButton size="small" variant="outlined" startIcon={<LuX />} onClick={cancelReply}>
            Cancel Reply
          </WsButton>
        )}
      </Grid>
      {enabledReply && (
        <Grid xs={12} sx={{ border: `1px solid ${WsColors.TertiaryShades200}`, borderRadius: 1 }}>
          <Grid item p={1}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {`From  : ${emailLog.direction !== 'OUT_BOUND' ? emailLog.to?.[0] : emailLog.from}`}
            </WsTypography>
          </Grid>
          <Grid item p={1} sx={{ borderTop: `1px solid ${WsColors.TertiaryShades200}` }}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {`To    : ${getToEmailAddress(emailLog)[0]}`}
            </WsTypography>
          </Grid>
          <WsEditor key="body" editor={editor} resizable={true} editorHeight={300} />
          <EmailFooter item xs={12}>
            {attachments.length > 0 && (
              <Grid container width={'fit-content'} alignItems="center" p={0.5}>
                {attachments.map((attachment: IBulkEmailAttachment, key: number) => {
                  if (key > 1 && !showAllAttachments) {
                    return;
                  } else {
                    return (
                      <Chip
                        key={key}
                        label={attachment.attachementName}
                        onDelete={() => onDeleteAttachment(key)}
                      />
                    );
                  }
                })}
                {!showAllAttachments && attachments.length - 2 > 0 && (
                  <WsTypography
                    variant={WsTypoVariants.BODY1}
                    color={WsTypoColors.PRIMARY}
                    onClick={() => setShowAllAttachments(true)}
                    cursor="pointer"
                  >
                    {`+${attachments.length - 2} More`}
                  </WsTypography>
                )}
                {showAllAttachments && (
                  <WsTypography
                    variant={WsTypoVariants.BODY1}
                    color={WsTypoColors.PRIMARY}
                    onClick={() => setShowAllAttachments(false)}
                    cursor="pointer"
                  >
                    Show Less
                  </WsTypography>
                )}
              </Grid>
            )}
            <Grid item display="flex" justifyContent="space-between" sx={{ p: 1 }}>
              <FileInput htmlFor="attachments">
                <input
                  name="attachments"
                  id="attachments"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => handleAttachments(e)}
                />
                <AttachmentIcon />
                <WsTypography
                  variant={WsTypoVariants.BODY1}
                  color={WsTypoColors.PRIMARY}
                  cursor="pointer"
                >
                  Attach File
                </WsTypography>
              </FileInput>
              <WsButton size="small" startIcon={<Send />} onClick={handleSendMail}>
                Send
              </WsButton>
            </Grid>
          </EmailFooter>
        </Grid>
      )}
    </Grid>
  );
};
